/**
 * ============================================================================
 * OosLog — Phase 7 frontend (Out-of-Specification Module).
 * Owner: Phase 7 — Out-of-Specification Module.
 * Route: /oos
 * Purpose: top-level OOS event log. Lists all OOS events for the tenant with
 *          two primary filters (status, kind) plus an optional batch-ref
 *          search. Clicking a row navigates to OosInvestigation (Phase 1 +
 *          Phase 2 tabs + closure form).
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md + tokens-v2):
 *   - Empty state has a primary CTA + governance copy.
 *   - Loading: skeleton rows (not a spinner-only).
 *   - Error: refined envelope with Retry button.
 *   - v2 tokens loaded via @edms/design-system/tokens.
 *
 * URS rows: OOS-010 (list view), OOS-002 (kind classification chip).
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './OosLog.module.css';
import { useOosListQuery } from './useOosListQuery';
import type { OosListItem, OosStatusValue, OosKindValue } from './useOosListQuery';
import { NewOosDialog } from './NewOosDialog';
import { AiResolutionTimeBadge } from '../../components/AiCluster';

const STATUS_VALUES: OosStatusValue[] = [
  'REPORTED',
  'PHASE_1',
  'PHASE_2',
  'CONCLUDED',
  'CLOSED',
  'CANCELLED',
];

const KIND_VALUES: OosKindValue[] = [
  'OUT_OF_SPEC',
  'OUT_OF_TREND',
  'OUT_OF_EXPECTATION',
];

export interface OosLogProps {
  /** Optional callback so the host shell can route to the detail page. */
  onOpenOos?: (id: string) => void;
}

export function OosLog({ onOpenOos }: OosLogProps = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<OosStatusValue | ''>('');
  const [kindFilter, setKindFilter] = useState<OosKindValue | ''>('');
  const [batchRef, setBatchRef] = useState('');
  const [showReport, setShowReport] = useState(false);

  const listQ = useOosListQuery({
    status: statusFilter || undefined,
    kind: kindFilter || undefined,
    batchRef: batchRef || undefined,
  });

  const items: OosListItem[] = listQ.data?.items ?? [];
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  const isEmpty = !listQ.isLoading && !listQ.isError && items.length === 0;

  function handleOpen(o: OosListItem) {
    if (onOpenOos) {
      onOpenOos(o.id);
      return;
    }
    navigate({ to: '/oos/$id', params: { id: o.id } });
  }

  function clearFilters() {
    setStatusFilter('');
    setKindFilter('');
    setBatchRef('');
  }

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('oos.log.eyebrow', { defaultValue: 'Quality Events' })}
        title={t('oos.log.title', { defaultValue: 'Out-of-Specification log' })}
        subtitle={t('oos.log.subtitle', {
          defaultValue:
            'FDA OOS Guidance Phase 1 / Phase 2 investigations across the tenant.',
        })}
        actions={
          <Button
            variant="primary"
            leadingIcon="+"
            onClick={() => setShowReport(true)}
            aria-label={t('oos.log.actions.addAria', {
              defaultValue: 'Report a new OOS event',
            })}
          >
            {t('oos.log.actions.add', { defaultValue: 'Report OOS' })}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <div className={styles.filterBar}>
            <label className={styles.filterField}>
              <span className={styles.filterLabel}>
                {t('oos.log.filters.status', { defaultValue: 'Status' })}
              </span>
              <select
                className={styles.filterSelect}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as OosStatusValue | '')}
              >
                <option value="">
                  {t('oos.log.filters.statusAny', { defaultValue: 'Any status' })}
                </option>
                {STATUS_VALUES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.filterField}>
              <span className={styles.filterLabel}>
                {t('oos.log.filters.kind', { defaultValue: 'Kind' })}
              </span>
              <select
                className={styles.filterSelect}
                value={kindFilter}
                onChange={(e) => setKindFilter(e.target.value as OosKindValue | '')}
              >
                <option value="">
                  {t('oos.log.filters.kindAny', { defaultValue: 'Any kind' })}
                </option>
                {KIND_VALUES.map((k) => (
                  <option key={k} value={k}>
                    {k.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.filterField}>
              <span className={styles.filterLabel}>
                {t('oos.log.filters.batch', { defaultValue: 'Batch ref' })}
              </span>
              <input
                className={styles.filterSelect}
                type="text"
                placeholder="BATCH-2026-…"
                value={batchRef}
                onChange={(e) => setBatchRef(e.target.value)}
              />
            </label>
            <button
              type="button"
              className={styles.filterClear}
              onClick={clearFilters}
              aria-label={t('oos.log.filters.clearAria', {
                defaultValue: 'Clear all filters',
              })}
            >
              {t('oos.log.filters.clear', { defaultValue: 'Clear' })}
            </button>
          </div>
        </CardHeader>
        <CardBody>
          {listQ.isError && (
            <div className={styles.errorBanner} role="alert">
              <span>
                {t('oos.log.error', {
                  defaultValue: 'Could not load OOS events.',
                })}
              </span>
              <Button variant="secondary" onClick={() => listQ.refetch?.()}>
                {t('common.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          )}

          {listQ.isLoading && (
            <>
              {[0, 1, 2].map((i) => (
                <div key={i} className={styles.skeletonRow} aria-hidden />
              ))}
            </>
          )}

          {isEmpty && (
            <div className={styles.emptyState}>
              <h2 className={styles.emptyTitle}>
                {t('oos.log.empty.title', { defaultValue: 'No OOS events yet' })}
              </h2>
              <p>
                {t('oos.log.empty.body', {
                  defaultValue:
                    'OOS events appear here as lab analysts report measurements that breach the registered specification. Use Report OOS to open the first one.',
                })}
              </p>
            </div>
          )}

          {!listQ.isLoading && !listQ.isError && items.length > 0 && (
            <div className={styles.tableWrap}>
              <table className={styles.table} aria-label="OOS events">
                <thead>
                  <tr>
                    <th>{t('oos.log.col.number', { defaultValue: 'OOS #' })}</th>
                    <th>{t('oos.log.col.status', { defaultValue: 'Status' })}</th>
                    <th>{t('oos.log.col.kind', { defaultValue: 'Kind' })}</th>
                    <th>{t('oos.log.col.sample', { defaultValue: 'Sample' })}</th>
                    <th>{t('oos.log.col.batch', { defaultValue: 'Batch' })}</th>
                    <th>{t('oos.log.col.result', { defaultValue: 'Result' })}</th>
                    <th>{t('oos.log.col.reported', { defaultValue: 'Reported' })}</th>
                    <th>{t('oos.log.col.cascades', { defaultValue: 'Cascades' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((o) => (
                    <tr key={o.id}>
                      <td>
                        <button
                          type="button"
                          className={styles.linkBtn}
                          onClick={() => handleOpen(o)}
                        >
                          {o.oosNumber}
                        </button>
                      </td>
                      <td>
                        <span className={`${styles.chip} ${statusChipClass(o.status)}`}>
                          {o.status}
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.chip} ${kindChipClass(o.outOfSpecKind)}`}>
                          {o.outOfSpecKind.replaceAll('_', ' ')}
                        </span>
                      </td>
                      <td className={styles.mono}>{o.sampleRef}</td>
                      <td className={styles.mono}>{o.batchRef ?? '—'}</td>
                      <td className={styles.resultCell}>
                        <span className={styles.resultValue}>
                          {nf.format(Number(o.resultValue))}
                        </span>
                        <span className={styles.resultUnits}>{o.resultUnits}</span>
                      </td>
                      <td>{dateFmt.format(new Date(o.reportedAtUtc))}</td>
                      <td>
                        {o.createsCapa && (
                          <span className={`${styles.chip} ${styles.chipCapa}`}>CAPA</span>
                        )}{' '}
                        {o.createsDeviation && (
                          <span className={`${styles.chip} ${styles.chipDev}`}>DEV</span>
                        )}
                        {/* AI-203 — resolution-time prediction badge */}
                        {o.status !== 'CLOSED' && o.status !== 'CANCELLED' && o.status !== 'CONCLUDED' && (
                          <>
                            {' '}
                            <AiResolutionTimeBadge
                              recordKind="oos"
                              recordId={o.id}
                              context={`${o.outOfSpecKind} for ${o.sampleRef} batch ${o.batchRef ?? 'n/a'} (result ${o.resultValue} ${o.resultUnits})`}
                              slaDays={o.outOfSpecKind === 'OUT_OF_SPEC' ? 14 : 21}
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {showReport && (
        <NewOosDialog
          onClose={() => setShowReport(false)}
          onCreated={() => {
            setShowReport(false);
            listQ.refetch?.();
          }}
        />
      )}
    </div>
  );
}

function statusChipClass(status: OosStatusValue): string {
  switch (status) {
    case 'REPORTED':
      return styles.statusReported;
    case 'PHASE_1':
      return styles.statusPhase1;
    case 'PHASE_2':
      return styles.statusPhase2;
    case 'CONCLUDED':
      return styles.statusConcluded;
    case 'CLOSED':
      return styles.statusClosed;
    case 'CANCELLED':
      return styles.statusCancelled;
    default:
      return '';
  }
}

function kindChipClass(kind: OosKindValue): string {
  switch (kind) {
    case 'OUT_OF_SPEC':
      return styles.kindOos;
    case 'OUT_OF_TREND':
      return styles.kindOot;
    case 'OUT_OF_EXPECTATION':
      return styles.kindOoe;
    default:
      return '';
  }
}
